// Native variant-media filtering for the product gallery.
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, false));
        });

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.filterVariantMedia(this.dataset.selectedMediaId || null, true);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateSliderCounter(event.detail.currentElement);
      }

      filterVariantMedia(variantFeaturedMediaId, prepend = true) {
        if (!this.elements.viewer) return;

        const selectedMediaId = variantFeaturedMediaId
          ? `${this.dataset.section}-${variantFeaturedMediaId}`
          : null;

        const viewerItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));

        viewerItems.forEach((item) => {
          const isFirstProductImage = item.dataset.originalPosition === '1';
          const belongsToVariant = item.dataset.variantMedia === 'true';
          const belongsToSelectedVariant = selectedMediaId && item.dataset.mediaId === selectedMediaId;
          const shouldShow = !isFirstProductImage && (!belongsToVariant || belongsToSelectedVariant);

          item.hidden = !shouldShow;
          item.classList.toggle('hidden', !shouldShow);
          item.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        });

        const thumbnailItems = Array.from(
          this.elements.thumbnails?.querySelectorAll('li[data-target]') || []
        );

        thumbnailItems.forEach((item) => {
          const isFirstProductImage = item.dataset.originalPosition === '1';
          const belongsToVariant = item.dataset.variantMedia === 'true';
          const belongsToSelectedVariant = selectedMediaId && item.dataset.target === selectedMediaId;
          const shouldShow = !isFirstProductImage && (!belongsToVariant || belongsToSelectedVariant);

          item.hidden = !shouldShow;
          item.classList.toggle('hidden', !shouldShow);
          item.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        });
        // Prioritize the selected main image before loading additional gallery images.
        if (selectedMediaId) {
          const selectedItem = viewerItems.find(
            (item) => item.dataset.mediaId === selectedMediaId
          );

          const selectedImage = selectedItem?.querySelector(
            '.product__modal-opener--image img'
          );

          if (selectedImage) {
            selectedImage.loading = 'eager';
            selectedImage.fetchPriority = 'high';

            // Start loading the best responsive image immediately.
            if (selectedImage.dataset.src) {
              selectedImage.src = selectedImage.dataset.src;
            }

            if (selectedImage.dataset.srcset) {
              selectedImage.srcset = selectedImage.dataset.srcset;
            }
          }
        }

        const visibleViewerItems = viewerItems.filter((item) => !item.hidden);
        const visibleThumbnailItems = thumbnailItems.filter((item) => !item.hidden);

        visibleViewerItems.forEach((item) => {
          item.classList.toggle('product__media-item--single', visibleViewerItems.length === 1);
        });

        visibleThumbnailItems.forEach((item, index) => {
          item.dataset.mediaPosition = index + 1;
        });

        const sliderButtons = this.elements.viewer.querySelector('.slider-buttons');
        if (sliderButtons) sliderButtons.hidden = visibleViewerItems.length <= 1;

        if (this.elements.thumbnails) {
          this.elements.thumbnails.hidden = visibleThumbnailItems.length <= 1;
        }

        this.refreshSliders();

        const selectedMedia = selectedMediaId
          ? visibleViewerItems.find((item) => item.dataset.mediaId === selectedMediaId)
          : null;
        const mediaToActivate = selectedMedia || visibleViewerItems[0];

        if (mediaToActivate) {
          this.setActiveMedia(mediaToActivate.dataset.mediaId, prepend, true);
        } else {
          viewerItems.forEach((item) => item.classList.remove('is-active'));
          thumbnailItems.forEach((item) => item.querySelector('button')?.removeAttribute('aria-current'));
          this.updateSliderCounter();
        }
      }

      setActiveMedia(mediaId, prepend = false, preventPageScroll = false) {
        const requestedMedia = this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`);
        const activeMedia =
          requestedMedia && !requestedMedia.hidden
            ? requestedMedia
            : Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []).find(
                (item) => !item.hidden
              );

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstElementChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${activeMedia.dataset.mediaId}"]`
            );
            if (activeThumbnail && activeThumbnail.parentElement.firstElementChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          this.refreshSliders();
        }

        if (!preventPageScroll) {
          this.preventStickyHeader();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }

        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }

        this.updateSliderCounter(activeMedia);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail || thumbnail.hidden) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        const thumbnailList = this.elements.thumbnails.slider;
        const isVertical = thumbnailList && window.getComputedStyle(thumbnailList).flexDirection === 'column';

        thumbnailList?.scrollTo(
          isVertical ? { top: thumbnail.offsetTop, behavior: 'smooth' } : { left: thumbnail.offsetLeft, behavior: 'smooth' }
        );
      }

      updateSliderCounter(activeMedia = null) {
        const visibleItems = Array.from(
          this.elements.viewer?.querySelectorAll('li[data-media-id]') || []
        ).filter((item) => !item.hidden);
        const activeItem = activeMedia || visibleItems.find((item) => item.classList.contains('is-active'));
        const activeIndex = activeItem ? visibleItems.indexOf(activeItem) + 1 : 0;

        const current = this.elements.viewer?.querySelector('.slider-counter--current');
        const total = this.elements.viewer?.querySelector('.slider-counter--total');

        if (current) current.textContent = activeIndex || 0;
        if (total) total.textContent = visibleItems.length;
      }

      refreshSliders() {
        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();

        window.requestAnimationFrame(() => {
          this.elements.viewer?.resetPages?.();
          this.elements.thumbnails?.resetPages?.();
        });
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const announceImage = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        if (image.complete) {
          announceImage();
        } else {
          image.addEventListener('load', announceImage, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
